import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, ChevronLeft, ChevronRight, Sparkles, Timer, ShoppingCart, CalendarClock } from "lucide-react";
import { scrollToId } from "@/lib/scroll";
import { DealCountdown } from "@/components/DealCountdown";

const fmt = new Intl.NumberFormat("en-IN", { maximumFractionDigits: 2 });
const BG = {
  purple: "from-[#4C1D95] to-[#7C3AED]", gold: "from-[#92400E] to-[#D97706]", emerald: "from-[#064E3B] to-[#059669]", rose: "from-[#881337] to-[#E11D48]",
  deal: "from-[#1E1B4B] to-[#4C1D95]", steps: "from-[#5B21B6] to-[#9333EA]", consult: "from-[#0F172A] to-[#334155]",
};

const buildSlides = (announcement, opportunities) => {
  const slides = [];
  if (announcement?.enabled) slides.push({ id: "announce", bg: BG[announcement.theme] || BG.purple, icon: Sparkles, eyebrow: announcement.tag || "Announcement", title: announcement.title, body: announcement.message, cta: announcement.cta_label || "Explore", target: announcement.cta_target || "#opportunities" });
  const deals = opportunities.filter((o) => o.closes_at && new Date(o.closes_at) > Date.now()).sort((a, b) => new Date(a.closes_at) - new Date(b.closes_at)).slice(0, 3);
  deals.forEach((o) => slides.push({ id: `deal-${o.slug}`, bg: BG.deal, icon: Timer, eyebrow: "Closing soon", title: o.name, body: `₹${fmt.format(o.price)} · ${o.sector}`, closesAt: o.closes_at, cta: "Buy now", target: `/company/${o.slug}#buy` }));
  slides.push({ id: "steps", bg: BG.steps, icon: ShoppingCart, eyebrow: "Simple", title: "Buy in 3 simple steps", body: "Pick quantity → review → pay securely. No KYC needed to start.", cta: "See the board", target: "#opportunities" });
  slides.push({ id: "consult", bg: BG.consult, icon: CalendarClock, eyebrow: "Expert desk", title: "Talk to a pre-IPO expert", body: "Book a 1:1 call and get a personalised allocation plan.", cta: "Book a slot", target: "#consult" });
  return slides;
};

export const HomeCarousel = ({ announcement, opportunities = [] }) => {
  const slides = useMemo(() => buildSlides(announcement, opportunities), [announcement, opportunities]);
  const [i, setI] = useState(0);
  const [dir, setDir] = useState(1);
  const navigate = useNavigate();

  useEffect(() => { if (i >= slides.length) setI(0); }, [slides.length, i]);
  useEffect(() => {
    if (slides.length < 2) return undefined;
    const t = setInterval(() => { setDir(1); setI((n) => (n + 1) % slides.length); }, 5000);
    return () => clearInterval(t);
  }, [slides.length]);

  const go = (n) => { setDir(n > i ? 1 : -1); setI((n + slides.length) % slides.length); };
  const open = (target) => (target.startsWith("/") ? navigate(target) : scrollToId(target));
  const s = slides[i];
  if (!s) return null;
  const Icon = s.icon;

  return (
    <section data-testid="home-carousel" className="mx-auto max-w-7xl px-6 pb-4 pt-2 lg:px-10">
      <div className="relative overflow-hidden rounded-2xl">
        <AnimatePresence initial={false} custom={dir} mode="popLayout">
          <motion.div
            key={s.id}
            custom={dir}
            initial={{ x: dir * 80, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ x: -dir * 80, opacity: 0 }}
            transition={{ duration: 0.45, ease: "easeOut" }}
            drag="x"
            dragConstraints={{ left: 0, right: 0 }}
            dragElastic={0.2}
            onDragEnd={(_, info) => { if (info.offset.x < -60) go(i + 1); else if (info.offset.x > 60) go(i - 1); }}
            data-testid={`carousel-slide-${s.id}`}
            className={`relative flex min-h-[168px] cursor-grab flex-col justify-between gap-5 bg-gradient-to-br ${s.bg} p-6 text-white sm:min-h-[180px] sm:flex-row sm:items-center sm:gap-6 sm:p-8 active:cursor-grabbing`}
          >
            <div className="pointer-events-none absolute -right-10 -top-10 h-44 w-44 rounded-full bg-white/10 blur-2xl" />
            <div className="relative min-w-0">
              <p className="flex items-center gap-1.5 font-mono2 text-[10px] uppercase tracking-[0.2em] text-white/70"><Icon className="h-3.5 w-3.5 shrink-0" /> <span className="line-clamp-1">{s.eyebrow}</span></p>
              <h3 className="font-display mt-2 line-clamp-2 text-2xl font-semibold tracking-tight sm:text-3xl">{s.title}</h3>
              {s.body && <p className="mt-1.5 max-w-lg text-sm text-white/80">{s.body}</p>}
              {s.closesAt && <div className="mt-3"><DealCountdown closesAt={s.closesAt} onDark testid={`carousel-countdown-${s.id}`} /></div>}
            </div>
            <button data-testid={`carousel-cta-${s.id}`} onClick={() => open(s.target)} className="relative flex shrink-0 items-center gap-2 self-start rounded-full bg-white px-5 py-3 sm:self-auto font-mono2 text-[10px] font-semibold uppercase tracking-[0.2em] text-[#4C1D95] transition-transform hover:scale-[1.03]">
              {s.cta} <ArrowRight className="h-3.5 w-3.5" />
            </button>
          </motion.div>
        </AnimatePresence>
        {slides.length > 1 && (
          <>
            <button data-testid="carousel-prev" onClick={() => go(i - 1)} aria-label="Previous" className="absolute left-3 top-1/2 hidden -translate-y-1/2 rounded-full bg-white/15 p-2 text-white backdrop-blur transition-colors hover:bg-white/30 md:block"><ChevronLeft className="h-4 w-4" /></button>
            <button data-testid="carousel-next" onClick={() => go(i + 1)} aria-label="Next" className="absolute right-3 top-1/2 hidden -translate-y-1/2 rounded-full bg-white/15 p-2 text-white backdrop-blur transition-colors hover:bg-white/30 md:block"><ChevronRight className="h-4 w-4" /></button>
          </>
        )}
      </div>
      {slides.length > 1 && (
        <div data-testid="carousel-dots" className="mt-3 flex justify-center gap-1.5">
          {slides.map((sl, n) => (
            <button key={sl.id} data-testid={`carousel-dot-${n}`} onClick={() => go(n)} aria-label={`Slide ${n + 1}`} className={`h-1.5 rounded-full transition-all duration-300 ${n === i ? "w-6 bg-[#6D28D9]" : "w-1.5 bg-slate-300 hover:bg-slate-400"}`} />
          ))}
        </div>
      )}
    </section>
  );
};
